import { useEffect, type RefObject } from 'react';

/**
 * Forwards wheel events over the fixed left column to the scrolling right pane.
 *
 * Without this, the left column is a dead zone: the pointer is over content that cannot
 * scroll, so the page appears frozen. § UI/UX asks for the forwarding, with the caveat
 * that the sidebar consumes its own overflow first — on a short viewport the left column
 * scrolls, and hijacking that would trap the user.
 */
export function useWheelForwarding(
  sideRef: RefObject<HTMLElement | null>,
  paneRef: RefObject<HTMLElement | null>,
  isSplit: boolean,
): void {
  useEffect(() => {
    const side = sideRef.current;
    const pane = paneRef.current;

    if (!isSplit || !side || !pane) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      // The sidebar has its own overflow to consume: leave it alone.
      if (side.scrollHeight > side.clientHeight + 1) {
        return;
      }

      pane.scrollTop += event.deltaY;
      event.preventDefault();
    };

    // Cannot be passive: it calls preventDefault.
    side.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      side.removeEventListener('wheel', onWheel);
    };
  }, [sideRef, paneRef, isSplit]);
}
